/* eslint-disable class-methods-use-this */
import axios from "axios";

// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths, import/order
import packageJson from "../../package.json";

import { getPersistedSession } from "components";
import ENV from "config/Env";

import type { AxiosRequestConfig } from "axios";

export const RESOURCE_NOT_FOUND = "resource_missing";

class Api {
  private static _instance: Api | null = null;

  constructor() {
    if (Api._instance) {
      return Api._instance;
    }

    Api._instance = this;
    axios.defaults.headers.common["X-Claire-Clientversion"] = packageJson.version;
    axios.defaults.headers.common["X-Claire-Clientplatform"] = "web";
    this.setupInterceptors();
  }

  private progressBarHooks: ((progress: number) => void)[] = [];

  private updateProgress(progress: number) {
    this.progressBarHooks.forEach(hook => hook(progress));
  }

  private setupInterceptors() {
    axios.interceptors.request.use(config => {
      config.onUploadProgress = progressEvent => {
        const progress = Math.round((progressEvent.loaded * 100) / Number(progressEvent.total));
        this.updateProgress(progress);
      };

      return config;
    });

    axios.interceptors.response.use(
      response => {
        window.setTimeout(() => {
          this.updateProgress(100);
          window.setTimeout(() => this.updateProgress(0), 300);
        }, 100);

        return response;
      },
      error => {
        this.updateProgress(0);
        throw error;
      }
    );
  }

  public addProgressBarHook(hook: (progress: number) => void) {
    this.progressBarHooks.push(hook);
  }

  public removeProgressBarHook(hook: (progress: number) => void) {
    this.progressBarHooks = this.progressBarHooks.filter(existingHook => existingHook !== hook);
  }

  private handleErrorMessage(error: unknown) {
    let errorMessage = "";

    if (axios.isAxiosError(error)) {
      if (error.response) {
        if (Array.isArray(error.response.data?.errors) && error.response.data.errors.length > 0) errorMessage = error.response.data.errors[0];
        else errorMessage = JSON.stringify(error.response.data);
      } else errorMessage = error.message;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    } else {
      errorMessage = String(error);
    }

    return errorMessage;
  }

  private requestConfig(): AxiosRequestConfig | undefined {
    const session = getPersistedSession();
    if (!session) return {};

    return {
      headers: {
        Authorization: `Bearer ${session.token}`,
        Pragma: "no-cache"
      }
    };
  }

  async get(path: string, baseUrl: string = ENV.appointmentBaseURL, config?: any): Promise<any> {
    try {
      const result = await axios.get(baseUrl + path, { ...this.requestConfig(), ...config });
      return result.data;
    } catch (error) {
      throw new Error(this.handleErrorMessage(error));
    }
  }

  async post(path: string, requestData?: any, baseUrl: string = ENV.appointmentBaseURL, config?: any): Promise<any> {
    try {
      const result = await axios.post(baseUrl + path, requestData, { ...this.requestConfig(), ...config });

      return result.data;
    } catch (error) {
      throw new Error(this.handleErrorMessage(error));
    }
  }
}

export const ApiInstance = new Api();
